import { useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Box, Divider, InputAdornment, Link, TextField, Typography } from '@mui/material';
import { Email } from '@mui/icons-material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import LoadingButton from '@mui/lab/LoadingButton';
import { Link as RouterLink } from 'react-router-dom';
import packageJson from '../../../../package.json';
import { ENVIRONMENT } from '../../../config';
import { BUILD_NO } from '../../../buildInfo';
import { PasswordInput } from '../../../components/_components/Inputs/PasswordInput';
import type { LoginFormValues } from '../schemas/loginSchema';
import { GoogleSignInButton } from './GoogleSignInButton';

const isDevelopment = ENVIRONMENT === 'development';

const loginInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    '&.Mui-focused': {
      backgroundColor: '#fff',
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    backgroundColor: 'transparent',
  },
} as const;

type LoginFormProps = {
  loading: boolean;
  onLogin: (values: LoginFormValues) => void;
  onGoogleSignIn: () => void;
  onForgotPasswordClick: () => void;
};

export function LoginForm({ loading, onLogin, onGoogleSignIn, onForgotPasswordClick }: LoginFormProps) {
  const { control, handleSubmit } = useFormContext<LoginFormValues>();

  useEffect(() => {
    if (isDevelopment) {
      document.title = BUILD_NO;
    }
  }, []);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit((values) => void onLogin(values))}
      sx={{
        // Flex parent on LoginPage centers this — avoid top/left/transform
        // which break when Local vs QA viewport/zoom/#root height differ.
        width: '100%',
        maxWidth: { xs: '100%', sm: 420, md: 440 },
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          width: '100%',
          flexShrink: 0,
          backgroundColor: 'transparent',
          padding: { xs: 0, sm: '40px' },
          paddingBottom: '10px',
          borderRadius: '5px',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: '100%',
            flexShrink: 0,
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: { md: '25px', lg: '30px', sm: '25px' },
              fontWeight: 'bolder',
              color: 'primary.main',
            }}
          >
            Welcome Back!
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: '90%', mt: '5px', mb: 2 }}>
            <GoogleSignInButton onClick={() => void onGoogleSignIn()} />
          </Box>
        </Box>

        <Box
          sx={{
            marginBottom: '5px',
            marginTop: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ width: '90%' }}>
            <Divider textAlign="center">
              <Typography sx={{ color: 'input.label' }}>or</Typography>
            </Divider>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box id="credentialBox" sx={{ width: '90%', mt: '5px', mb: 2 }}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  id="email"
                  fullWidth
                  size="small"
                  type="email"
                  label={
                    <>
                      Email{' '}
                      <Typography component="span" sx={{ color: 'danger.main' }}>
                        *
                      </Typography>
                    </>
                  }
                  placeholder="Enter Your Email"
                  autoFocus
                  autoComplete="on"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={loginInputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: '90%' }}>
            <Controller
              name="password"
              control={control}
              render={({ field, fieldState }) => (
                <PasswordInput
                  {...field}
                  id="password"
                  label={
                    <>
                      Password{' '}
                      <Typography component="span" sx={{ color: 'danger.main' }}>
                        *
                      </Typography>
                    </>
                  }
                  autoComplete="on"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  sx={loginInputSx}
                />
              )}
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '6px',
              marginBottom: '10px',
              width: '90%',
            }}
          >
            <Link
              id="FrPasswrd"
              component="button"
              type="button"
              sx={{
                fontSize: '15px',
                textDecoration: 'none',
                fontWeight: '600',
                color: 'text.textClr',
                marginBottom: 0,
                cursor: 'pointer',
              }}
              onClick={() => void onForgotPasswordClick()}
            >
              Forgot Password ?
            </Link>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px',
              width: '90%',
            }}
          >
            <LoadingButton
              sx={{
                width: '100%',
                height: '40px',
                borderRadius: '5px',
              }}
              loading={loading}
              disableRipple
              type="submit"
              variant="contained"
              color="primary"
            >
              Log In
            </LoadingButton>
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            marginTop: '10px',
            alignItems: 'center',
            justifyContent: 'space-around',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '5px' }}>
            <Typography>Don&apos;t have an account?</Typography>
            <Link
              id="signUp"
              component={RouterLink}
              to="/Create_Account"
              sx={{ color: 'text.Link', textDecoration: 'none' }}
            >
              <Typography component="span" sx={{ fontWeight: 'bold' }}>
                Sign up
                <ArrowOutwardIcon sx={{ fontSize: '14px', verticalAlign: 'middle' }} />
              </Typography>
            </Link>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mt: '24px', mb: '15px' }}>
          <Typography color="grey">Version {packageJson.version}</Typography>
          {isDevelopment && <Typography color="grey">{BUILD_NO}</Typography>}
        </Box>
      </Box>
    </Box>
  );
}
